import {ContentEditableMarkdownEditor} from '@frontapp/editor-html';
import {debounce, isUndefined, noop, without} from 'lodash';
import React, {Component, createRef, MutableRefObject, ReactNode, RefCallback, RefObject} from 'react';
import styled, {css} from 'styled-components';
import {Key} from 'ts-key-enum';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {
  hasMetaKey,
  KeyboardActionsEnum,
} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {buildInteractionIdProps} from '../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  EventHandler,
  FrontKeyboardEvent,
  FrontMouseEvent,
  KeyboardEventHandler,
} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {stringSimilarity} from '../../../../../libs/shared/core/src/helpers/utilities/stringSimilarity';
import {
  isAttachmentModel,
  isUploadedAttachmentModel,
  UploadedAttachmentModel,
} from '../../../../../libs/shared/core/src/models/api/attachmentModel';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {SendActionsEnum} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {CommentComposerDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/composerDataEventObject';
import {PushProps, withPush} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {
  DataEventTrackingContextType,
  withDataEventTracking,
} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  EventEntryPointsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {KeyboardEventHandlers} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fonts, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  growToFitHeightContainerStyle,
  growToFitHeightStyle,
} from '../../../../../libs/shared/ui/src/styles/styleHelpers';
import {
  PrecogProps,
  PrecogUpdateTypesEnum,
  withCommentPrecog,
} from '../../../../../srcCommon/inboxes/composer/precogs/precogHooks';
import {startSimplePerformanceInteraction} from '../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {markFullStorySensitive} from '../../../../helpers/fullstoryHelpers';
import {fetchGifFile} from '../../../../helpers/giphyHelpers';
import {CommentWarning, findMentionedAliases} from '../../../../helpers/models/commentHelpers';
import {
  AnnotationCommentHighlightModel,
  AnnotationMessageHighlightModel,
  AnnotationModel,
  RCommentHighlightAnnotation,
  RMessageHighlightAnnotation,
} from '../../../../models/api/annotationModel';
import {
  AnnotationEventTrackingSourcesEnum,
  AnnotationTypesEnum,
} from '../../../../models/api/annotationTypes';
import {CommentModel} from '../../../../models/api/commentModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {GifModel} from '../../../../models/api/gifModel';
import {registerCommentComposer} from '../../../../services/composers';
import {EmojiMap} from '../../../../services/emojis/emojis';
import {CommentWarningsError} from '../../../../store/data/comments/sendCommentAction';
import {AttachmentsDropzone} from '../../../common/attachments/attachmentsDropzone';
import {AttachmentsPasteTarget} from '../../../common/attachments/attachmentsPasteTarget';
import {AttachmentFileUploaderOptions} from '../../../common/attachments/attachmentsTypes';
import {AttachmentsTransferProps, AttachmentsUploader} from '../../../common/attachments/attachmentsUploader';
import {ChangeManager} from '../../../common/editor/change/changeManager';
import {createEditorServiceChangeManager} from '../../../common/editor/change/editorServiceChangeManager';
import {ContentEditable, EditorTypesEnum} from '../../../common/editor/contentEditable';
import {EmojiEditorDropdown} from '../../../common/editor/dropdowns/emojiEditorDropdown';
import {MentionEditorDropdown} from '../../../common/editor/dropdowns/mentionEditorDropdown';
import {insertEmojiInEditor} from '../../../common/editor/plugins/text/emojiPlugin';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {CommentComposerInfoBubble} from '../../../infoBubbles/commentComposerInfoBubble';
import {StartGuideCommentComposerInfoBubble} from '../../../infoBubbles/startGuideCommentComposerInfoBubble';
import {MentionEducationTipCoordinator} from '../../../onboarding/sandbox/educationTips/mentionEducationTipCoordinator';
import {SafetyNetActionsEnum} from '../conversation/safetyNets/safetyNetsContext';
import {SafetyNetsCoordinator} from '../conversation/safetyNets/safetyNetsCoordinator';
import {CommentComposerAnnotationPreviewContainer} from './commentComposerAnnotationPreviewContainer';
import {CommentComposerAttachments} from './commentComposerAttachments';
import {CommentComposerControlsContainer} from './commentComposerControlsContainer';
import {CommentComposerFooter} from './commentComposerFooter';
import {CommentComposerFormattingPopover} from './commentComposerFormattingPopover';
import {CommentComposerReplyPreviewContainer} from './commentComposerReplyPreviewContainer';
import {CommentComposerWarningDialog} from './commentComposerWarningDialog';
import {createCommentComposerEditor} from './createCommentComposerEditor';

/*
 * Props.
 */

export interface SendCommentOptions {
  sendAction: SendActionsEnum;
  inboxId?: number;
  ignoreWarnings?: boolean;
}

export interface UpdateAnnotationOptions {
  annotation: AnnotationModel;
  activityId: number;
}

export interface CommentComposerPublicProps {
  panelId: ViewerPanelId;
  className?: string;
  company: CompanyModel;
  user: TeammateModel;
  conversation: ConversationModel | null;
  comment: CommentModel;
  isEditing?: boolean;
  referencedActivityId?: number;
  annotation?: AnnotationModel;
  editLastComment: () => void;
  endCommentEdit: () => void;
  updateReferencedActivity?: (arg: number | null) => void;
  updateAnnotation?: (options: UpdateAnnotationOptions) => void;
  mentionableAliases: ReadonlySet<string>;
  onTextChange: (newText: string) => void;
  sendComment: (options: SendCommentOptions) => Promise<void>;
  addAttachment: (model: UploadedAttachmentModel) => void;
  attachments: CommentModel['attachments'];
  onRemoveAttachmentClick: (arg: string) => void;
  isDisabled?: boolean;
  isAIDisabled?: boolean;
  isSendDisabled?: boolean;
  shouldFocus?: boolean;
  isExpanded: boolean;
  onExpandedToggle: (arg: EventEntryPointsEnum) => void;
  onBlurIfEmpty?: () => void;
  shouldHideExpandButton?: boolean;
  shouldHideCommentComposerInfoBubble?: boolean;
  hasContentHeight?: boolean;
  placeholder?: ReactNode;
  sendLabel?: ReactNode;
  emojiValidationMap: EmojiMap;
  hasAutoShareDrafts: boolean;
  setComposerHeight?: (arg: number) => void;
  cellsContext: CellsContext | undefined;
  hasNewSynchronousP3: boolean;
}

type CommentComposerProps = CommentComposerPublicProps &
  PrecogProps &
  PushProps &
  DataEventTrackingContextType;

/*
 * Style.
 */

export const mentionClassName = 'front-decoration-mention';

const StyledComposerDiv = styled.div`
  ${growToFitHeightContainerStyle}
  overflow: hidden;
`;

const StyledMentionEducationTipCoordinator = styled(MentionEducationTipCoordinator)`
  ${growToFitHeightContainerStyle}
  overflow: hidden;
`;

interface ContentDivProps {
  $isEditing: boolean;
}
const StyledContentDiv = styled.div<ContentDivProps>`
  flex: auto;

  display: flex;
  flex-direction: column;
  padding: ${(p) => (p.$isEditing ? '0px' : '16px 14px 0px')};
  overflow: hidden;

  ${(p) => addAdditionalStyles(p)};
`;

function addAdditionalStyles(props: ContentDivProps) {
  if (props.$isEditing) {
    return '';
  }

  return css`
    padding-left: 6px;
    flex-direction: row;
  `;
}

interface BubbleDivProps {
  $isActive?: boolean;
  $isDisabled?: boolean;
}

const StyledBubbleDiv = styled.div<BubbleDivProps>`
  flex: auto;

  display: flex;
  flex-direction: column;
  border-radius: 17px;
  overflow: hidden;

  ${(p) => buildBubbleStyle(p)}
`;

function buildBubbleStyle(props: BubbleDivProps) {
  if (props.$isDisabled) {
    return disabledBubbleStyle;
  }

  if (props.$isActive) {
    return activeBubbleStyle;
  }

  return normalBubbleStyle;
}

const disabledBubbleStyle = css`
  border: 2px solid ${(p) => p.theme.greys.shade30};
  background-color: ${(p) => p.theme.commentComposer.background};
`;

const activeBubbleStyle = css`
  border: 2px solid ${(p) => p.theme.palette.blue.shade40};
  background-color: ${(p) => p.theme.commentComposer.backgroundFocused};
`;

const normalBubbleStyle = css`
  border: 1px solid transparent;
  background-color: ${(p) => p.theme.commentComposer.background};
  padding: 1px;

  &:hover {
    background-color: ${(p) => p.theme.commentComposer.backgroundHover};
  }
`;

const StyledBoxDropzone = styled(AttachmentsDropzone)`
  flex: auto;

  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

interface BoxDivProps {
  $hasTopBorder: boolean;

  $hasFocus?: boolean;
}

const StyledBoxDiv = styled.div<BoxDivProps>`
  flex: auto;

  ${(p) => (p.$hasTopBorder ? `border-top: 1px solid ${p.theme.greys.shade30};` : '')}

  display: flex;
  flex-direction: row;
  overflow: hidden;

  ${(p) => addAdditionalBoxStyles(p)}
`;

function addAdditionalBoxStyles(props: BoxDivProps) {
  if (!props.$hasTopBorder) {
    return '';
  }

  if (props.$hasFocus) {
    return css`
      border-top: 1px solid ${(p) => p.theme.greys.shade40};
    `;
  }

  return css`
    border-top: 1px solid ${(p) => p.theme.alphas.gray20};
  `;
}

const lineHeightInPixels = 20;

const StyledEditorDiv = styled.div`
  flex: auto;

  ${growToFitHeightContainerStyle};
  line-height: ${lineHeightInPixels}px;
  overflow: auto;

  .${mentionClassName} {
    color: ${(p) => p.theme.interactiveText};
    font-weight: ${fontWeights.medium};
  }
`;

const StyledAttachmentsPasteTarget = styled(AttachmentsPasteTarget)`
  ${growToFitHeightStyle};
  ${growToFitHeightContainerStyle};
`;

const contentVerticalPaddingInPixels = 5;
// TODO: Reduce duplication with CommentBody and CommentSummary and mobileEditor.html styles.
const contentStyle = css`
  color: ${(p) => p.theme.commentComposer.text};
  padding: ${contentVerticalPaddingInPixels}px 10px;

  blockquote {
    padding-left: 10px;
    margin: 0;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background-color: ${(p) => p.theme.greys.shade50};
      border-radius: 2px;
    }
  }

  ol,
  ul {
    margin: 0;
    padding-left: 1.5em;
  }

  li {
    margin: 0;
  }

  code {
    padding: 2px;
    margin: -2px 0;

    border-radius: 4px;
    border: 1px solid ${(p) => p.theme.palette.blue.shade20};
    color: ${(p) => p.theme.interactiveText};
    font-family: ${fonts.monospace};
    background-color: ${(p) => p.theme.palette.blue.shade10};

    /* Causes the browser to paint the cursor more clearly inside or outside the inline code. */
    ::before,
    ::after {
      vertical-align: text-top;
      display: inline-block;
      content: '';
    }
  }

  pre {
    margin: 0;
    white-space: pre-wrap;

    > code {
      display: block;
      padding: 2px 6px;
      margin: 0;

      ::before,
      ::after {
        display: none;
      }
    }
  }
`;

interface EditorContentProps {
  $isExpanded: boolean;
  $hasContentHeight: boolean | undefined;
}

const StyledContentEditable = styled(ContentEditable)<EditorContentProps>`
  ${growToFitHeightStyle};
  ${contentStyle};

  ${(p) => (p.$hasContentHeight ? buildContentHeightStyle(p.$isExpanded) : undefined)};
`;

function buildContentHeightStyle(isExpanded: boolean) {
  // When expanded, offer a minimum of 15 lines.
  if (isExpanded) {
    return css`
      min-height: ${5 * lineHeightInPixels + 2 * contentVerticalPaddingInPixels}px;
    `;
  }

  // When collapsed, force a scrollbar after 12 lines to distinguish from the expanded state.
  return css`
    max-height: ${12 * lineHeightInPixels + 2 * contentVerticalPaddingInPixels}px;
  `;
}

const StyledDisabledContentDiv = styled.div`
  ${growToFitHeightStyle};
  ${contentStyle};
  color: ${(p) => p.theme.greys.shade50};
`;

const StyledPlaceholderOverlayDiv = styled.div`
  position: absolute;
  pointer-events: none;
`;

const StyledPlaceholderDiv = styled.div`
  ${contentStyle};
  color: ${(p) => p.theme.commentComposer.placeholder};
`;

const StyledSideDiv = styled.div`
  flex: none;

  display: flex;
  flex-direction: column;
  /* Align items to the right edge. */
  align-items: flex-end;
  /* Justify items to top and bottom edges. */
  justify-content: space-between;
  overflow: hidden;
`;

const StyledButtonsDiv = styled.div`
  display: flex;
`;

const StyledButton = styled(Button)`
  flex: none;

  margin: 4px;
`;

const StyledTeammateAvatar = styled(TeammateAvatar)`
  margin-right: 8px;
  margin-top: 2px;
`;

/*
 * Component.
 */

interface CommentSendAttempt {
  warnings: ReadonlyArray<CommentWarning>;
  sendAction: SendActionsEnum;
}

interface CommentComposerState {
  sendAttempt?: CommentSendAttempt;
  isControlDropdownOpen?: boolean;
}

type FileUploadHandler = (file: File, options?: AttachmentFileUploaderOptions) => void;

/* eslint react/no-unused-state: 0 */
class CommentComposerInternal extends Component<CommentComposerProps, CommentComposerState> {
  constructor(props: CommentComposerProps) {
    super(props);
    this.state = {};

    this.editor = createCommentComposerEditor(
      props.comment.text,
      () => this.props.conversation?.id,
      mentionClassName,
      () => this.props.mentionableAliases,
      () => this.props.isExpanded,
      this.props.emojiValidationMap,
    );
    this.changeManager = createEditorServiceChangeManager(this.editor.services.markdown);
    this.wasTextSaved = false;
    this.isSending = false;
    this.boxDropzoneWrapperRef = createRef<HTMLDivElement>();
    this.styledComposerWrapperRef = createRef<HTMLDivElement>();
  }

  private readonly editor: ContentEditableMarkdownEditor;
  private readonly changeManager: ChangeManager;
  private isSending: boolean;
  private wasTextSaved: boolean;
  private boxDropzoneWrapperRef: RefObject<HTMLDivElement>;
  private styledComposerWrapperRef: RefObject<HTMLDivElement>;
  private unregisterComposer?: () => void;

  // For tracking only. Used to track how much an auto summary is edited before sending.
  private autoSummarizeMessage: string | null = null;

  // This is the only way to create a mutable ref object in a class component.
  private readonly contentEditableRef: MutableRefObject<HTMLDivElement | null> = {current: null};
  private readonly contentEditableRefCallback: RefCallback<HTMLDivElement | null> = (element) => {
    this.contentEditableRef.current = element;
  };

  componentDidMount() {
    if (this.props.shouldFocus) {
      this.editor.services.focus.focus();
      if (this.props.isEditing) {
        // Needed when editing a comment so the cursor will start at the end of the text
        this.editor.services.selection.moveToEnd();
      }
    }

    this.changeManager.subscribe((isDirty) => {
      // Avoid saving a previously unsaved draft when a non-UI change was made.
      if (!this.wasTextSaved && !isDirty) {
        return;
      }

      this.saveTextDebounced(this.props.comment.uid);

      // Don't update the precog if we're sending the message.
      if (this.isSending) {
        return;
      }

      const shouldStopPrecog = this.isEmpty();
      const updateType = shouldStopPrecog ? PrecogUpdateTypesEnum.STOP : PrecogUpdateTypesEnum.CONTINUE;

      this.props.updatePrecog(updateType);
    });

    this.registerComposer();
  }

  private registerComposer() {
    if (this.unregisterComposer) {
      this.unregisterComposer();
    }

    // Allows other components to access the composer's API to focus or set the referenced activity for replies
    // Not needed for inline editing because other components don't need access to the API
    if (this.props.conversation?.id && !this.props.isEditing) {
      this.unregisterComposer = registerCommentComposer(this.props.conversation?.id, this);
    }
  }

  componentDidUpdate(prevProps: Readonly<CommentComposerProps>) {
    const {comment, conversation} = this.props;
    const isUpdateFromOtherPanel = comment.lastPanelId && comment.lastPanelId !== this.props.panelId;
    const shouldUpdateComment = isUpdateFromOtherPanel || comment.uid !== prevProps.comment.uid;
    const shouldRegisterComposer = conversation?.id !== prevProps.conversation?.id;

    if (shouldUpdateComment) {
      this.wasTextSaved = false;
      this.changeManager.markAsPristine(() => {
        this.editor.services.markdown.setMarkdown(comment.text);
        this.editor.services.selection.moveToEnd();
      });
    }

    if (shouldRegisterComposer) {
      this.registerComposer();
    }
  }

  componentWillUnmount() {
    this.changeManager.unsubscribe();

    // Flush any pending saves in case we get unmounted without any user interaction, such as after a conversation join.
    this.saveTextDebounced.flush();

    if (this.props.setComposerHeight) {
      this.props.setComposerHeight(0);
    }

    if (this.unregisterComposer) {
      this.unregisterComposer();
    }
  }

  /*
   * Editor event handlers.
   */

  private readonly saveTextDebounced = debounce((commentUid: string) => {
    // If the comment has changed, nothing to do.
    if (commentUid !== this.props.comment.uid) {
      return;
    }

    // If we're in edit mode, do not autosave.
    if (this.props.isEditing) {
      return;
    }

    this.saveText();
  }, 1000);

  private saveText() {
    this.wasTextSaved = true;

    // Cancel any pending saves when we are called directly.
    this.saveTextDebounced.cancel();

    // Save the comment text.
    const text = this.getMarkdown();
    this.props.onTextChange(text);
  }

  private readonly onEditorBlur: () => void = () => {
    // Flush any pending changes when an editor loses focus.
    this.saveTextDebounced.flush();

    const {referencedActivityId, attachments, onBlurIfEmpty} = this.props;
    if (!onBlurIfEmpty) {
      return;
    }

    // Notify when an empty editor blurs.
    if (this.isEmpty() && isUndefined(referencedActivityId) && attachments.length === 0) {
      onBlurIfEmpty();
    }
  };

  private readonly onEditorKeyDown = (event: FrontKeyboardEvent) => {
    switch (event.key) {
      case Key.Escape:
        this.onEditorEscape(event);
        break;

      case Key.ArrowUp:
        this.onEditorUpArrow(event);
        break;

      default:
      // Do nothing.
    }
  };

  // This should be used as a KeyboardShortcuts handler (and not a React onKeyDown handler) so that it plays nice:
  // - The mention dropdown needs to be able to override the CONFIRM shortcut (Enter key), which it can do by mounting a
  //   KeyboardShortcuts component after us.
  // - We need to be able to handle Enter key events (to send the comment) before the editor does (to add a line break),
  //   which uses native events and would otherwise run before React's synthetic events.
  private onSendKeyboardAction(event: FrontKeyboardEvent, areAllAttachmentsUploaded: boolean) {
    if (event.defaultPrevented) {
      return;
    }

    const sendAction = findSendActionForEnterEvent(event, this.props.isExpanded);
    if (!sendAction) {
      return;
    }

    event.preventDefault();

    // Can't save/send if there's no content, for a new comment.
    const {isSendDisabled, conversation} = this.props;
    if (isSendDisabled || !this.canSendComment() || !areAllAttachmentsUploaded) {
      return;
    }

    startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.SEND_COMMENT);

    this.saveOrSendComment(sendAction);
    this.trackSendEvent(EventEntryPointsEnum.KEYBOARD_SHORTCUT);
    this.props.trackDataEvent(DataEventActionsEnum.CLICK, CommentComposerDataEventObject.COMMENT_SEND, {
      is_keyboard_shortcut: true,
      conversation_id: conversation?.id,
    });
  }

  private onEditorEscape(event: FrontKeyboardEvent) {
    // The Esc key can either dismiss an autocomplete dropdown or blur the composer.
    const wasBlurred = !this.editor.services.focus.isActive;
    if (!wasBlurred || !this.props.isEditing) {
      return;
    }

    event.preventDefault();

    // Stop editing a comment.
    this.props.endCommentEdit();
  }

  private onEditorUpArrow(event: FrontKeyboardEvent) {
    // We can only edit if the input is empty.
    if (!this.isEmpty()) {
      return;
    }

    // If so, edit the last comment.
    event.preventDefault();
    this.props.editLastComment();
  }

  private readonly onSideMouseDown = (event: FrontMouseEvent) => {
    // If the event targeted a child node, ignore.
    if (event.target !== event.currentTarget) {
      return;
    }

    // Focus the editor (and avoid blurring it) when we mouse down on dead space on the side.
    event.preventDefault();
    this.editor.services.focus.focus();
  };

  private saveOrSendComment(sendAction: SendActionsEnum) {
    // If this is an edit, just save the comment.
    if (this.props.isEditing) {
      this.saveText();
      return;
    }

    this.sendComment({sendAction});
  }

  private trackSendEvent(entryPoint: EventEntryPointsEnum) {
    let quotedMessageSource;
    if (this.props.annotation?.type === AnnotationTypesEnum.HIGHLIGHT_COMMENT) {
      quotedMessageSource = AnnotationEventTrackingSourcesEnum.COMMENT;
    } else if (this.props.annotation?.type === AnnotationTypesEnum.HIGHLIGHT_MESSAGE) {
      quotedMessageSource = AnnotationEventTrackingSourcesEnum.EMAIL;
    } else {
      quotedMessageSource = null;
    }

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_SEND_ICON_CLICK, {
      conversation_id: this.props.conversation ? this.props.conversation.id : undefined,
      namespace: this.props.conversation?.namespace ?? null,
      entry_point: entryPoint,
      comment_id: this.props.comment.id ?? null,
      is_reply: Boolean(
        this.props.comment.referencedActivityId !== undefined && this.props.annotation === undefined,
      ),
      is_quote_comment: Boolean(
        this.props.comment.referencedActivityId !== undefined &&
          this.props.annotation?.type === AnnotationTypesEnum.HIGHLIGHT_COMMENT,
      ),
      is_new: !(this.props.isEditing ?? false),
      quoted_message_source: quotedMessageSource,
    });
  }

  private readonly onSendClick: EventHandler = (event) => {
    startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.SEND_COMMENT);
    this.saveOrSendComment(SendActionsEnum.SEND);
    this.trackSendEvent(EventEntryPointsEnum.MANUAL);
  };

  private canSendComment() {
    const {attachments, annotation, isEditing} = this.props;
    const text = this.getMarkdown();

    // Highlight comment annotations cannot be sent without text/attachments, just like replies.
    const hasIndependentAnnotation = Boolean(
      annotation && annotation.type !== AnnotationTypesEnum.HIGHLIGHT_COMMENT,
    );

    // Emptying the comment during editing should be allowed to delete the message.
    return isEditing || text.length > 0 || attachments.length > 0 || hasIndependentAnnotation;
  }

  private maybeTrackSummarizeSendSimilarity(sentText: string) {
    if (!this.autoSummarizeMessage) {
      return;
    }

    const similarity = stringSimilarity(this.autoSummarizeMessage, sentText);
    this.autoSummarizeMessage = null;

    if (similarity === null) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_AI_SUMMARIZE_SEND_SIMILARITY, {
      similarity,
    });
  }

  private readonly sendComment = async (options: SendCommentOptions) => {
    const {comment} = this.props;

    // Process emoticons etc. that are still under the cursor.
    await this.editor.services.commit.commitAsync();

    if (!this.canSendComment()) {
      return;
    }

    // Force a server save before sending if comment is offline.
    if (comment.error) {
      this.saveTextDebounced(comment.uid);
    }

    // Flush any pending changes.
    this.saveTextDebounced.flush();

    this.isSending = true;
    try {
      // `sendComment()` clears the editor. Get the text now before it's cleared.
      const {text} = this.editor.services.text;
      await this.props.sendComment(options);
      this.maybeTrackSummarizeSendSimilarity(text);
    } catch (err) {
      // If this error is only warnings, handle.
      if (err instanceof CommentWarningsError) {
        this.setState({
          sendAttempt: {
            warnings: err.warnings,
            sendAction: options.sendAction,
          },
        });
        return;
      }

      throw err;
    } finally {
      this.isSending = false;
    }
  };

  private readonly onEmojiSelect: (arg: string) => void = (shortcode) => {
    insertEmojiInEditor(this.editor, shortcode);
  };

  private readonly onGifSelect = async (gif: GifModel, onFileUpload: FileUploadHandler) => {
    const file = await fetchGifFile(gif);
    onFileUpload(file, {giphyId: gif.id});

    this.editor.services.focus.focus();
  };

  // Focus the composer from attachments and emoji select
  private readonly onControlDropdownOpen = (isOpen?: boolean) => {
    if (!isUndefined(isOpen)) {
      this.setState({isControlDropdownOpen: isOpen});
    }
    if (!isOpen) {
      this.editor.services.focus.focus();
    }
  };

  private readonly onMentionClick = () => {
    const mentionToken = '@';
    const {text} = this.editor.services.text;

    const emptyStringOrSpace = !text.length || text.endsWith(' ');

    if (emptyStringOrSpace) {
      this.editor.services.edit.insertText(mentionToken);
    }

    if (!emptyStringOrSpace && !text.endsWith(mentionToken)) {
      this.editor.services.edit.insertText(` ${mentionToken}`);
    }

    this.editor.services.focus.focus();

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_MENTION_ICON_CLICK, {
      conversation_id: this.props.conversation ? this.props.conversation.id : undefined,
    });
  };

  private readonly onInsertCommentComposerText = (textToInsert: string) => {
    const {text} = this.editor.services.text;
    const emptyStringOrSpace = !text.length || text.endsWith(' ');

    this.editor.services.edit.insertText(emptyStringOrSpace ? textToInsert : ` ${textToInsert}`);
    this.editor.services.focus.focus();
  };

  private readonly onExpandToggleClick = () => {
    this.editor.services.focus.focus();

    if (this.props.shouldHideExpandButton) {
      return;
    }

    this.props.onExpandedToggle(EventEntryPointsEnum.MANUAL);
  };

  private readonly onInsertSummarizeText = (textToInsert: string) => {
    this.onInsertCommentComposerText(textToInsert);
    this.autoSummarizeMessage = this.editor.services.text.text;
  };

  private readonly onExpandToggleKeyboardAction = (event: FrontKeyboardEvent) => {
    // Because our KeyboardShortcuts has noPreventDefault.
    event.preventDefault();

    this.props.onExpandedToggle(EventEntryPointsEnum.KEYBOARD_SHORTCUT);
  };

  private readonly focusComment = (event: FrontKeyboardEvent) => {
    // Because our KeyboardShortcuts has noPreventDefault.
    event.preventDefault();

    this.editor.services.focus.focus();
  };

  private isEmpty() {
    const {markdown} = this.editor.services.markdown;
    return markdown.length === 0;
  }

  private getMarkdown() {
    const {markdown} = this.editor.services.markdown;
    return markdown.trim();
  }

  /*
   * API.
   */

  focus() {
    this.editor.services.focus.focus();
  }

  setReferencedActivity(id: number | null) {
    if (this.props.updateReferencedActivity) {
      this.props.updateReferencedActivity(id);
    }
  }

  setAnnotation(annotation: AnnotationModel, activityId: number) {
    if (this.props.updateAnnotation) {
      this.props.updateAnnotation({annotation, activityId});
    }
  }

  /*
   * Warnings event handlers.
   */

  private readonly onWarningCancelClick: (attempt: CommentSendAttempt) => void = (sendAttempt) => {
    this.setState((state) => {
      // If the send attempt already changed, nothing to do.
      if (state.sendAttempt !== sendAttempt) {
        return null;
      }

      // Otherwise, remove it.
      return {sendAttempt: undefined};
    });
  };

  private readonly onWarningOkClick = (sendAttempt: CommentSendAttempt, warning: CommentWarning) => {
    this.setState((state) => {
      // If the send attempt already changed, nothing to do.
      if (state.sendAttempt !== sendAttempt) {
        return null;
      }

      // If this wasn't the last warning, update the send attempt.
      const {sendAction, warnings} = sendAttempt;
      const newWarnings = without(warnings, warning);
      if (newWarnings.length > 0) {
        return {sendAttempt: {...sendAttempt, warnings: newWarnings}};
      }

      // Otherwise, send the comment and clear the attempt.
      globalWindow.setInstrumentedTimeout(() => this.sendComment({sendAction, ignoreWarnings: true}), 0);
      return {sendAttempt: undefined};
    });
  };

  /*
   * Render.
   */

  render() {
    if (this.props.isDisabled) {
      return this.renderDisabledComposer();
    }

    return (
      <ServiceSubscription source={this.editor.services.focus}>
        {({isActive}) => this.renderEnabledComposer(isActive)}
      </ServiceSubscription>
    );
  }

  private renderEnabledComposer(isActive: boolean) {
    const {company, addAttachment} = this.props;
    const attachments = this.props.attachments.filter(isAttachmentModel);

    return (
      <AttachmentsUploader company={company} attachments={attachments} onAttachmentUploaded={addAttachment}>
        {(attachmentsProps) => this.renderEnabledComposerContent(attachmentsProps, isActive)}
      </AttachmentsUploader>
    );
  }

  private renderEnabledComposerContent(attachmentsProps: AttachmentsTransferProps, isActive: boolean) {
    const {panelId, conversation, referencedActivityId, annotation} = this.props;
    const areAllAttachmentsUploaded = attachmentsProps.attachments.every((a) => isUploadedAttachmentModel(a));
    const hasReferencedActivity = Boolean(referencedActivityId);
    const hasReply = isReply(annotation, hasReferencedActivity);
    const hasAttachments = attachmentsProps.attachments.length > 0;
    const hasFocus = this.state.isControlDropdownOpen || isActive;

    const onSendKeyboardAction: KeyboardEventHandler = (event) =>
      this.onSendKeyboardAction(event, areAllAttachmentsUploaded);

    const composerInteractionIdProps =
      hasReply && annotation === undefined ? buildInteractionIdProps('comment-composer-full-reply') : {};

    return (
      <SafetyNetsCoordinator
        conversations={conversation ? [conversation] : []}
        onActionValidated={onSendKeyboardAction}
        safetyNetActionType={SafetyNetActionsEnum.COMMENT_AND_ARCHIVE}
        cellsContext={this.props.cellsContext}
      >
        {(onSendAndDone) => {
          // Register some handlers---and override keyboard shortcuts from other components---only when the editor has focus.
          const keyHandlers: KeyboardEventHandlers = isActive
            ? {
                [KeyboardActionsEnum.TOGGLE_EXPANDED_COMMENT_COMPOSER]: this.onExpandToggleKeyboardAction,
                [KeyboardActionsEnum.CONFIRM]: onSendKeyboardAction,
                [KeyboardActionsEnum.SEND]: onSendKeyboardAction,
                [KeyboardActionsEnum.SEND_AND_DONE]: onSendAndDone,
              }
            : {[KeyboardActionsEnum.FOCUS_COMMENT]: this.focusComment};

          return (
            <KeyboardShortcuts handlers={keyHandlers} noPreventDefault>
              <StyledMentionEducationTipCoordinator
                className={this.props.className}
                conversationId={conversation?.id}
                isEditingExistingComment={this.props.isEditing ?? false}
              >
                <StyledComposerDiv
                  ref={this.styledComposerWrapperRef}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...composerInteractionIdProps}
                >
                  <StyledContentDiv $isEditing={Boolean(this.props.isEditing)}>
                    {!this.props.isEditing && !this.props.hasNewSynchronousP3 && (
                      <StyledTeammateAvatar size={VisualSizesEnum.LARGE} teammate={this.props.user} />
                    )}
                    <StyledBubbleDiv $isActive={hasFocus} ref={this.boxDropzoneWrapperRef}>
                      <StyledBoxDropzone
                        shouldScroll
                        onDropFile={(file) => {
                          attachmentsProps.onFileUpload(file);
                          this.editor.services.focus.focus();
                        }}
                        onDropAttachmentItems={(items) => {
                          attachmentsProps.onDropAttachmentItems(items);
                          this.editor.services.focus.focus();
                        }}
                        size={VisualSizesEnum.SMALL}
                      >
                        {isHighlightMessageAnnotation(annotation) && referencedActivityId && (
                          <CommentComposerAnnotationPreviewContainer
                            annotation={annotation}
                            referencedActivityId={referencedActivityId}
                            onRemoveAnnotationClick={() => {
                              this.setReferencedActivity(null);
                            }}
                          />
                        )}
                        {isReply(annotation, hasReferencedActivity) && (
                          <CommentComposerReplyPreviewContainer
                            annotation={annotation}
                            referencedActivityId={referencedActivityId}
                            onRemoveReplyClick={() => {
                              this.setReferencedActivity(null);
                            }}
                          />
                        )}
                        {hasAttachments && (
                          <CommentComposerAttachments
                            onRemoveAttachmentClick={this.props.onRemoveAttachmentClick}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...attachmentsProps}
                          />
                        )}
                        <StyledBoxDiv $hasTopBorder={hasAttachments || hasReply} $hasFocus={isActive}>
                          <StyledEditorDiv
                            className={markFullStorySensitive()}
                            onKeyDown={this.onEditorKeyDown}
                          >
                            <StyledAttachmentsPasteTarget onPasteFile={attachmentsProps.onFileUpload}>
                              <ServiceSubscription source={this.editor.services.empty}>
                                {({isEmpty}) =>
                                  isEmpty && (
                                    <StyledPlaceholderOverlayDiv>
                                      <StyledPlaceholderDiv>{this.renderPlaceholder()}</StyledPlaceholderDiv>
                                    </StyledPlaceholderOverlayDiv>
                                  )
                                }
                              </ServiceSubscription>
                              <StyledContentEditable
                                $isExpanded={this.props.isExpanded}
                                $hasContentHeight={this.props.hasContentHeight}
                                refCallback={this.contentEditableRefCallback}
                                editor={this.editor}
                                onBlur={this.onEditorBlur}
                                hasGrammarlySupport
                                onHeightChange={() => {
                                  if (!this.props.setComposerHeight) {
                                    return;
                                  }
                                  const composerHeight =
                                    this.boxDropzoneWrapperRef.current?.getBoundingClientRect().height ?? 0;
                                  this.props.setComposerHeight(composerHeight);
                                }}
                                editorType={EditorTypesEnum.COMMENT}
                              />
                            </StyledAttachmentsPasteTarget>
                          </StyledEditorDiv>
                          {this.maybeRenderFormattingPopover()}
                          <EmojiEditorDropdown autocompleteService={this.editor.services.autocomplete} />
                          <MentionEditorDropdown
                            panelId={panelId}
                            conversation={conversation || undefined}
                            autocompleteService={this.editor.services.autocomplete}
                          />
                          <StyledSideDiv onMouseDown={this.onSideMouseDown}>
                            {this.renderControls(attachmentsProps.onFileUpload)}
                            <StyledButtonsDiv>
                              {this.maybeRenderCancelButton()}
                              {this.maybeRenderSendButton(areAllAttachmentsUploaded)}
                            </StyledButtonsDiv>
                          </StyledSideDiv>
                        </StyledBoxDiv>
                      </StyledBoxDropzone>
                    </StyledBubbleDiv>
                    {!this.props.shouldHideCommentComposerInfoBubble && (
                      <StartGuideCommentComposerInfoBubble anchor={this.boxDropzoneWrapperRef} />
                    )}
                  </StyledContentDiv>
                  {this.maybeRenderWarnings()}
                  {!this.props.isEditing && (
                    <ServiceSubscription source={this.editor.services.text}>
                      {({text}) => (
                        <CommentComposerFooter
                          hasNewSynchronousP3={this.props.hasNewSynchronousP3}
                          conversation={conversation}
                          mentionedAliases={findMentionedAliases(text)}
                        />
                      )}
                    </ServiceSubscription>
                  )}
                </StyledComposerDiv>
              </StyledMentionEducationTipCoordinator>
              {!this.props.shouldHideCommentComposerInfoBubble && (
                <CommentComposerInfoBubble anchor={this.styledComposerWrapperRef} />
              )}
            </KeyboardShortcuts>
          );
        }}
      </SafetyNetsCoordinator>
    );
  }

  private renderPlaceholder() {
    return (
      this.props.placeholder ||
      renderDefaultPlaceholder(Boolean(this.props.isEditing), this.props.hasNewSynchronousP3)
    );
  }

  private maybeRenderFormattingPopover() {
    return (
      <CommentComposerFormattingPopover
        conversationId={this.props.conversation?.id}
        editor={this.editor}
        contentEditableRef={this.contentEditableRef}
      />
    );
  }

  private renderDisabledComposer() {
    return (
      <StyledComposerDiv className={this.props.className}>
        <StyledContentDiv $isEditing={Boolean(this.props.isEditing)}>
          {!this.props.isEditing && (
            <StyledTeammateAvatar size={VisualSizesEnum.LARGE} teammate={this.props.user} />
          )}
          <StyledBubbleDiv $isDisabled>
            <StyledBoxDiv $hasTopBorder={false}>
              <StyledEditorDiv>
                <StyledDisabledContentDiv>
                  <Trans id="permission-comment-disabled">Commenting is disabled</Trans>
                </StyledDisabledContentDiv>
              </StyledEditorDiv>
              <StyledSideDiv>{this.renderControls(noop)}</StyledSideDiv>
            </StyledBoxDiv>
          </StyledBubbleDiv>
        </StyledContentDiv>
        <CommentComposerFooter
          conversation={this.props.conversation}
          hasNewSynchronousP3={this.props.hasNewSynchronousP3}
        />
      </StyledComposerDiv>
    );
  }

  private renderControls(onFileUpload: FileUploadHandler) {
    return (
      <CommentComposerControlsContainer
        conversationId={this.props.conversation ? this.props.conversation.id : undefined}
        conversationHasMessages={this.props.conversation ? this.props.conversation.numMessages > 0 : false}
        isDisabled={this.props.isDisabled}
        isAIDisabled={this.props.isAIDisabled}
        onFileUpload={onFileUpload}
        onEmojiSelect={this.onEmojiSelect}
        onGifSelect={(gif) => this.onGifSelect(gif, onFileUpload)}
        onMentionClick={this.onMentionClick}
        onInsertCommentComposerText={this.onInsertCommentComposerText}
        onInsertSummarizeText={this.onInsertSummarizeText}
        onControlDropdownOpen={this.onControlDropdownOpen}
        isExpanded={this.props.isExpanded}
        onExpandToggleClick={this.props.shouldHideExpandButton ? undefined : this.onExpandToggleClick}
      />
    );
  }

  private maybeRenderCancelButton() {
    if (!this.props.isEditing) {
      return null;
    }

    return (
      <StyledButton
        variant={ButtonVariantsEnum.LINK}
        size={VisualSizesEnum.SMALL}
        shouldPreventMouseDown
        onClick={this.props.endCommentEdit}
        interactionId="cancel"
      >
        Cancel
      </StyledButton>
    );
  }

  private maybeRenderSendButton(areAllAttachmentsUploaded: boolean) {
    if (!this.props.isExpanded && !this.props.isEditing) {
      return null;
    }

    const {isSendDisabled} = this.props;
    return (
      <ServiceSubscription source={this.editor.services.markdown}>
        {() => (
          <StyledButton
            variant={ButtonVariantsEnum.PRIMARY}
            size={VisualSizesEnum.SMALL}
            shouldPreventMouseDown
            isDisabled={isSendDisabled || !this.canSendComment() || !areAllAttachmentsUploaded}
            onClick={this.onSendClick}
            interactionId="send"
          >
            {this.renderSendLabel()}
          </StyledButton>
        )}
      </ServiceSubscription>
    );
  }

  private renderSendLabel() {
    return this.props.sendLabel || renderDefaultSendLabel(Boolean(this.props.isEditing));
  }

  /*
   * Render warnings.
   */

  private maybeRenderWarnings() {
    const {sendAttempt} = this.state;
    if (!sendAttempt) {
      return null;
    }

    const firstWarning = sendAttempt.warnings[0];
    return firstWarning && this.renderWarning(sendAttempt, firstWarning);
  }

  private renderWarning(sendAttempt: CommentSendAttempt, warning: CommentWarning) {
    const hasDrafts = Boolean(
      this.props.hasAutoShareDrafts &&
        this.props.conversation &&
        (this.props.conversation.draftMessageUids.length > 0 ||
          (this.props.conversation.unsavedDraftMessageUids || []).length > 0),
    );

    return (
      <CommentComposerWarningDialog
        warning={warning}
        onRequestClose={() => this.onWarningCancelClick(sendAttempt)}
        onOkClick={() => this.onWarningOkClick(sendAttempt, warning)}
        isInConversationWithDrafts={hasDrafts}
      />
    );
  }
}

function renderDefaultPlaceholder(isEditing: boolean, hasNewSynchronousP3: boolean) {
  if (isEditing) {
    return <Trans id="composer-comment-editplaceholder">Edit your comment</Trans>;
  }

  if (hasNewSynchronousP3) {
    return <Trans id="composer-comment-addplaceholder-new">Add internal comment</Trans>;
  }

  return <Trans id="composer-comment-addplaceholder">Add a comment or add others with @</Trans>;
}

function renderDefaultSendLabel(isEditing: boolean) {
  if (isEditing) {
    return <Trans id="commentcomposer-savebutton-expanded">Save</Trans>;
  }

  return <Trans id="commentcomposer-sendbutton-expanded">Send comment</Trans>;
}

export const CommentComposer = withPush(withCommentPrecog(withDataEventTracking(CommentComposerInternal)));

/*
 * Helpers.
 */

function isHighlightMessageAnnotation(
  annotation: AnnotationModel | undefined,
): annotation is AnnotationMessageHighlightModel {
  return Boolean(annotation && RMessageHighlightAnnotation.is(annotation));
}

function isReply(
  annotation: AnnotationModel | undefined,
  hasReferencedActivity: boolean,
): annotation is AnnotationCommentHighlightModel | undefined {
  const hasFullCommentReply = hasReferencedActivity && annotation === undefined;
  const hasQuoteCommentReply = hasReferencedActivity && RCommentHighlightAnnotation.is(annotation);
  return hasFullCommentReply || hasQuoteCommentReply;
}

function findSendActionForEnterEvent(
  event: FrontKeyboardEvent,
  isExpanded: boolean,
): SendActionsEnum | undefined {
  // Cmd+Enter should always send & archive.
  if (hasMetaKey(event) && !event.shiftKey) {
    return SendActionsEnum.SEND_AND_ARCHIVE;
  }

  return isExpanded
    ? findExpandedSendActionForEnterEvent(event)
    : findCollapsedSendActionForEnterEvent(event);
}

function findCollapsedSendActionForEnterEvent(event: FrontKeyboardEvent): SendActionsEnum | undefined {
  // Shift should prevent sending, and insert line breaks.
  if (event.shiftKey) {
    return undefined;
  }

  // Send by default.
  return SendActionsEnum.SEND;
}

function findExpandedSendActionForEnterEvent(event: FrontKeyboardEvent): SendActionsEnum | undefined {
  // Cmd+Shift+Enter should send.
  if (hasMetaKey(event) && event.shiftKey) {
    return SendActionsEnum.SEND;
  }

  // Insert line breaks by default.
  return undefined;
}
